import { useCallback, useMemo } from 'react';

import { useMemberId } from '@trello/authentication';

import { useAddOneTimeMessageDismissedMutation } from './AddOneTimeMessageDismissedMutation.generated';
import { useMyOneTimeMessagesDismissedFragment } from './MyOneTimeMessagesDismissedFragment.generated';

export const useOneTimeMessagesDismissed = () => {
  const memberId = useMemberId();
  const { data: member, complete } = useMyOneTimeMessagesDismissedFragment({
    from: { id: memberId },
    optimistic: true,
  });

  const [addOneTimeMessageDismissed] = useAddOneTimeMessageDismissedMutation();

  const oneTimeMessagesDismissed = useMemo(
    () => member?.oneTimeMessagesDismissed ?? [],
    [member?.oneTimeMessagesDismissed],
  );

  // This was added due to an issue where this hook was being called too early,
  // causing a temporary flash before all data was loaded. We want to assume messages
  // are dismissed by default (true) because UX is better for things to appear into view
  // than for things to flash and then disappear.
  const isOneTimeMessageDismissed = useCallback(
    (messageId: string) =>
      complete
        ? oneTimeMessagesDismissed.some((message) => message === messageId)
        : true,
    [oneTimeMessagesDismissed, complete],
  );

  const dismissOneTimeMessage = useCallback(
    async (
      messageId: string,
      options: { optimistic?: boolean } = { optimistic: true },
    ) => {
      if (isOneTimeMessageDismissed(messageId)) {
        return;
      }

      return addOneTimeMessageDismissed({
        variables: {
          memberId: memberId || 'me',
          messageId,
        },
        optimisticResponse: options?.optimistic
          ? {
              __typename: 'Mutation',
              addOneTimeMessagesDismissed: {
                id: memberId || 'me',
                oneTimeMessagesDismissed: oneTimeMessagesDismissed?.concat([
                  messageId,
                ]),
                __typename: 'Member',
              },
            }
          : undefined,
      });
    },
    [
      addOneTimeMessageDismissed,
      isOneTimeMessageDismissed,
      memberId,
      oneTimeMessagesDismissed,
    ],
  );

  return {
    oneTimeMessagesDismissed,
    dismissOneTimeMessage,
    isOneTimeMessageDismissed,
  };
};
